import { useEffect, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import { CheckCircle, XCircle, Info, X } from "lucide-react";

type ToastType = "success" | "error" | "info";

interface ToastItem {
    id: number;
    message: string;
    type: ToastType;
    duration: number;
}

// Global toast state and management
class ToastManager {
    private toasts: ToastItem[] = [];
    private idCounter = 0;
    private container: HTMLElement | null = null;
    private root: Root | null = null;
    private updateCallback: (() => void) | null = null;

    init() {
        if (!this.container && typeof window !== 'undefined') {
            this.container = document.createElement('div');
            this.container.id = 'toast-container';
            document.body.appendChild(this.container);
            this.root = createRoot(this.container);
            this.render();
        }
    }

    setUpdateCallback(callback: (() => void) | null) {
        this.updateCallback = callback;
    }

    add(message: string, type: ToastType = "info", duration: number = 3000): number {
        const id = ++this.idCounter;
        this.toasts.push({ id, message, type, duration });
        this.render();
        return id;
    }

    remove(id: number) {
        this.toasts = this.toasts.filter(t => t.id !== id);
        this.render();
    }

    clear() {
        this.toasts = [];
        this.render();
    }

    getToasts(): ToastItem[] {
        return [...this.toasts];
    }

    private render() {
        if (this.root) {
            this.root.render(<ToastContainer manager={this} />);
        }
        if (this.updateCallback) {
            this.updateCallback();
        }
    }
}

const toastManager = new ToastManager();

// Initialize on import
if (typeof window !== 'undefined') {
    toastManager.init();
}

// Toast Item Component
function ToastItemComponent({ 
    toast,
    onClose 
}: { 
    toast: ToastItem;
    onClose: () => void;
}) {
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        if (toast.duration > 0) {
            const timer = setTimeout(() => {
                setVisible(false);
                setTimeout(onClose, 300); // Wait for animation
            }, toast.duration);

            return () => clearTimeout(timer);
        }
    }, [toast.duration, onClose]);

    if (!visible) return null;

    const getIcon = () => {
        switch (toast.type) {
            case "success":
                return <CheckCircle className="h-5 w-5 text-green-600" />;
            case "error":
                return <XCircle className="h-5 w-5 text-red-600" />;
            case "info":
                return <Info className="h-5 w-5 text-blue-600" />;
        }
    };

    const getBgColor = () => {
        switch (toast.type) {
            case "success":
                return "bg-green-50 border-green-200";
            case "error":
                return "bg-red-50 border-red-200";
            case "info":
                return "bg-blue-50 border-blue-200";
        }
    };

    const getTextColor = () => {
        switch (toast.type) {
            case "success":
                return "text-green-800";
            case "error":
                return "text-red-800";
            case "info":
                return "text-blue-800";
        }
    };

    return (
        <div className={`${getBgColor()} border rounded-lg shadow-lg p-4 min-w-[300px] max-w-[500px] 
            flex items-center gap-3 transition-all duration-300 ease-in-out mb-3`}>
            <div className="flex-shrink-0">
                {getIcon()}
            </div>
            <p className={`flex-1 text-sm font-medium ${getTextColor()}`}>
                {toast.message}
            </p>
            <button
                onClick={() => {
                    setVisible(false);
                    setTimeout(onClose, 300);
                }}
                className={`flex-shrink-0 ${getTextColor()} hover:opacity-70 transition-opacity`}
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}

// Toast Container Component
function ToastContainer({ manager }: { manager: ToastManager }) {
    const [toastList, setToastList] = useState<ToastItem[]>(manager.getToasts());

    useEffect(() => {
        const updateToasts = () => {
            setToastList(manager.getToasts());
        };
        manager.setUpdateCallback(updateToasts);
        return () => {
            manager.setUpdateCallback(null);
        };
    }, [manager]);

    if (toastList.length === 0) return null;

    return (
        <div className="fixed z-[200] top-5 left-1/2 -translate-x-1/2 flex flex-col items-center">
            {toastList.map((toast) => (
                <ToastItemComponent
                    key={toast.id}
                    toast={toast}
                    onClose={() => manager.remove(toast.id)}
                />
            ))}
        </div>
    );
}

// Toast API - Simple and easy to use
export const toast = {
    show: (message: string, type: ToastType = "info", duration: number = 3000) => {
        return toastManager.add(message, type, duration);
    },
    success: (message: string, duration: number = 3000) => {
        return toastManager.add(message, "success", duration);
    },
    error: (message: string, duration: number = 3000) => {
        return toastManager.add(message, "error", duration);
    },
    info: (message: string, duration: number = 3000) => {
        return toastManager.add(message, "info", duration);
    },
    remove: (id: number) => {
        toastManager.remove(id);
    },
    clear: () => {
        toastManager.clear();
    }
};

// Export default Toast component for backward compatibility
export default function Toast({
    message,
    type = "info",
    duration = 3000,
    onClose,
    show = true
}: {
    message: string;
    type?: ToastType;
    duration?: number;
    onClose?: () => void;
    show?: boolean;
}) {
    const [visible, setVisible] = useState(show);

    useEffect(() => {
        setVisible(show);
    }, [show]);

    useEffect(() => {
        if (visible && duration > 0) {
            const timer = setTimeout(() => {
                setVisible(false);
                onClose?.();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [visible, duration, onClose]);

    if (!visible) return null;

    const getIcon = () => {
        switch (type) {
            case "success":
                return <CheckCircle className="h-5 w-5 text-green-600" />;
            case "error":
                return <XCircle className="h-5 w-5 text-red-600" />;
            case "info":
                return <Info className="h-5 w-5 text-blue-600" />;
        }
    };

    const getBgColor = () => {
        switch (type) {
            case "success":
                return "bg-green-50 border-green-200";
            case "error":
                return "bg-red-50 border-red-200";
            case "info":
                return "bg-blue-50 border-blue-200";
        }
    };

    const getTextColor = () => {
        switch (type) {
            case "success":
                return "text-green-800";
            case "error":
                return "text-red-800";
            case "info":
                return "text-blue-800";
        }
    };

    return (
        <div className={`fixed z-[200] top-5 left-1/2 -translate-x-1/2 
            ${getBgColor()} border rounded-lg shadow-lg p-4 min-w-[300px] max-w-[500px] 
            flex items-center gap-3 transition-all duration-300 ease-in-out`}>
            <div className="flex-shrink-0">
                {getIcon()}
            </div>
            <p className={`flex-1 text-sm font-medium ${getTextColor()}`}>
                {message}
            </p>
            {onClose && (
                <button
                    onClick={() => {
                        setVisible(false);
                        onClose();
                    }}
                    className={`flex-shrink-0 ${getTextColor()} hover:opacity-70 transition-opacity`}
                >
                    <X className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}

// Hook for easy usage (backward compatibility)
export function useToast() {
    return {
        showToast: (message: string, type: ToastType = "info") => {
            toast.show(message, type);
        },
        hideToast: () => {
            toast.clear();
        },
        ToastComponent: null // No longer needed
    };
}
